import React, { useState, useMemo, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toEditKey } from '../../Hooks/Price-simulation/useWorksheetAdjustments';
import type { EditTarget, WorksheetAdjustments } from '../../Hooks/Price-simulation/useWorksheetAdjustments.types';
import { withoutKey } from '../../Utils/objectUtils';
import AdvancedAdjustmentPopover from './AdvancedAdjustmentPopover';
import type { AdjustmentValue, AdjustmentSpec, AdjustmentScope, ScopePriceFlags } from './PriceSimulation.types';
import type { CalculatedGroup, CalculatedProduct, Calculations, InputMode, ModalState, WorksheetInputEditor } from './PriceSimulationWorksheet.types';
import { applyKeptToPlanEntry, applySpecToPlanEntry } from './bulkAdjustment';
import type { BulkApplyPlan } from './bulkAdjustment';
import { buildVisibleInstancesMap, hasIncludedPrice, hasScalablePrice, pickBulkReferencePlan, toEditableBulkPlans } from './worksheetCalculations';
import { calculateFilteredProgress } from './roundProgress';
import type { SkuInstance } from './skuInstances';
import { countDistinctPackages } from './skuInstances';
import WorksheetGroupRows from './WorksheetGroupRows';
import WorksheetProductRow from './WorksheetProductRow';
import WorksheetSummary from './WorksheetSummary';
import WorksheetTierCard from './WorksheetTierCard';

interface PriceSimulationWorksheetProps {
    calculations: Calculations;
    adjustmentState: WorksheetAdjustments;
    skuInstancesMap: Record<string, SkuInstance[]>;
    onFilterBySku: (sku: string) => void;
}

const CLOSED_MODAL: ModalState = { isOpen: false, product: null, plan: null, group: null, triggerRef: null };

const PriceSimulationWorksheet = ({ calculations, adjustmentState, skuInstancesMap, onFilterBySku }: PriceSimulationWorksheetProps) => {
    const { adjustments, getInputMode, toggleInputMode, commitInput, toggleKept, applyAdjustments } = adjustmentState;
    const [editingInputs, setEditingInputs] = useState<Record<string, string>>({});
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [collapsedTiers, setCollapsedTiers] = useState<Record<string, boolean>>({});
    const [modalState, setModalState] = useState<ModalState>(CLOSED_MODAL);

    const filteredProgress = useMemo(() => calculateFilteredProgress(calculations), [calculations]);

    const visibleInstancesMap = useMemo(() => buildVisibleInstancesMap(calculations), [calculations]);

    const editor = useMemo<WorksheetInputEditor>(
        () => ({
            getInputMode,
            getEditingValue: (target: EditTarget) => editingInputs[toEditKey(target)],
            onChange: (target: EditTarget, value: string) => setEditingInputs((prev) => ({ ...prev, [toEditKey(target)]: value })),
            onBlur: (target: EditTarget, mode: InputMode, currentPrice: number) => {
                const key = toEditKey(target);
                const rawValue = editingInputs[key];
                if (rawValue === undefined) return;
                commitInput(target, mode, currentPrice, rawValue);
                setEditingInputs((prev) => withoutKey(prev, key));
            },
            onToggleMode: (target: EditTarget) => {
                setEditingInputs((prev) => withoutKey(prev, toEditKey(target)));
                toggleInputMode(target);
            },
            onToggleKept: toggleKept,
        }),
        [getInputMode, editingInputs, commitInput, toggleInputMode, toggleKept]
    );

    const toggle = (setCollapsed: Dispatch<SetStateAction<Record<string, boolean>>>, key: string) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleAdjustAllPlans = useCallback((product: CalculatedProduct, group: CalculatedGroup, event: React.MouseEvent<HTMLElement>) => {
        const referencePlan = pickBulkReferencePlan(product.plans);
        if (referencePlan === null) return;
        setModalState({
            isOpen: true,
            product,
            plan: referencePlan,
            group: { ...group, name: group.groupName },
            triggerRef: { current: event.currentTarget },
        });
    }, []);

    const handleCloseModal = useCallback(() => setModalState(CLOSED_MODAL), []);

    const handleModalApply = useCallback(
        (spec: AdjustmentSpec | null, scope: AdjustmentScope, includedPrice: number | null, keepCurrent: boolean) => {
            const { product } = modalState;
            if (!product) return;

            const allTierInstances = visibleInstancesMap[product.productSku] ?? [];

            applyAdjustments((prev) => {
                const next = { ...prev };
                const applyTo = (productId: string, plans: BulkApplyPlan[]) => {
                    const entries: Record<string, AdjustmentValue> = { ...next[productId] };
                    plans.forEach((pl) => (keepCurrent ? applyKeptToPlanEntry(entries, pl, true) : applySpecToPlanEntry(entries, pl, spec, includedPrice)));
                    next[productId] = entries;
                };

                if (scope === 'allTiers') {
                    allTierInstances.forEach((inst) => applyTo(inst.productId, toEditableBulkPlans(inst.plans)));
                } else {
                    applyTo(product.id, toEditableBulkPlans(product.plans));
                }

                return next;
            });
        },
        [modalState, visibleInstancesMap, applyAdjustments]
    );

    // Mirrors the plans handleModalApply touches for each scope, so the popover never offers an adjustment
    // that the chosen scope cannot apply.
    const modalPriceFlags = useMemo<Record<AdjustmentScope, ScopePriceFlags>>(() => {
        const toFlags = (plans: BulkApplyPlan[]): ScopePriceFlags => ({ hasIncluded: plans.some(hasIncludedPrice), hasScalable: plans.some(hasScalablePrice) });
        const { product } = modalState;
        if (!product) return { thisTier: toFlags([]), allTiers: toFlags([]) };
        return {
            thisTier: toFlags(toEditableBulkPlans(product.plans)),
            allTiers: toFlags((visibleInstancesMap[product.productSku] ?? []).flatMap((inst) => toEditableBulkPlans(inst.plans))),
        };
    }, [modalState, visibleInstancesMap]);

    const relatedInstances = useMemo(() => {
        const { product, plan } = modalState;
        if (!product) return [];
        return (visibleInstancesMap[product.productSku] ?? [])
            .filter((inst) => inst.productId !== product.id && inst.plans.some((p) => p.status === 'Draft'))
            .map((inst) => ({
                groupName: inst.groupName,
                productName: inst.productName,
                currentPrice: inst.plans.find((p) => p.name === plan?.name)?.currentPrice ?? 0,
            }));
    }, [modalState, visibleInstancesMap]);

    return (
        <div className="mt-24">
            <WorksheetSummary
                totalImpact={calculations.totalImpact}
                subscriptionImpact={calculations.subscriptionImpact}
                transactionImpact={calculations.transactionImpact}
                billedImpact={calculations.billedImpact}
                hasQuantity={calculations.hasQuantity}
                currentMrr={calculations.currentMrr}
                newMrr={calculations.newMrr}
                weightedIncrease={calculations.weightedIncrease}
                excludedTieredPlanCount={calculations.excludedTieredPlanCount}
                excludedTieredGrossRevenue={calculations.excludedTieredGrossRevenue}
                progress={filteredProgress}
            />

            <div className="d-flex flex-column gap-32">
                {calculations.packages.map((pkg) => (
                    <React.Fragment key={pkg.packageId}>
                        {pkg.tiers.map((tier) => (
                            <WorksheetTierCard
                                key={tier.tierKey}
                                packageName={pkg.packageName}
                                metricsPeriodStart={pkg.metricsPeriodStart}
                                metricsPeriodEnd={pkg.metricsPeriodEnd}
                                tier={tier}
                                isCollapsed={collapsedTiers[tier.tierKey] ?? false}
                                onToggle={() => toggle(setCollapsedTiers, tier.tierKey)}
                            >
                                {tier.productGroups.map((group, groupIndex) => (
                                    <WorksheetGroupRows
                                        key={group.groupKey}
                                        group={group}
                                        isFirst={groupIndex === 0}
                                        isCollapsed={collapsedGroups[group.groupKey] ?? false}
                                        onToggle={() => toggle(setCollapsedGroups, group.groupKey)}
                                    >
                                        {group.products.map((product) => (
                                            <WorksheetProductRow
                                                key={product.id}
                                                product={product}
                                                tierCount={(skuInstancesMap[product.productSku] ?? []).length}
                                                packageCount={countDistinctPackages(skuInstancesMap[product.productSku] ?? [])}
                                                onFilterBySku={onFilterBySku}
                                                onAdjustAllPlans={(event) => handleAdjustAllPlans(product, group, event)}
                                                editor={editor}
                                            />
                                        ))}
                                    </WorksheetGroupRows>
                                ))}
                            </WorksheetTierCard>
                        ))}
                    </React.Fragment>
                ))}
            </div>

            <AdvancedAdjustmentPopover
                isOpen={modalState.isOpen}
                onClose={handleCloseModal}
                onApply={handleModalApply}
                product={modalState.product}
                plan={modalState.plan}
                group={modalState.group}
                triggerRef={modalState.triggerRef}
                priceFlags={modalPriceFlags}
                relatedInstances={relatedInstances}
                currentAdjustment={modalState.product && modalState.plan ? (adjustments[modalState.product.id]?.[modalState.plan.id]?.percentage ?? 0) : 0}
            />
        </div>
    );
};

export default PriceSimulationWorksheet;
